/**
 * Using for recomendation products and recent view products
 */
if (!customElements.get('product-slider')) {
  customElements.define(
    'product-slider',
    class ProductSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (!FoxTheme.utils) return;
        this.selectors = {
          productsWrap: '.products-grid-wrap',
          products: '.products-grid',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.elements.section = this.closest(`.section-${this.sectionId}`);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);
        this.totalItems = parseInt(this.dataset.totalItems);
        this.paginationType = this.dataset.paginationType || 'bullets';

        this.sliderInstance = false;

        if (!this.enableSlider) return;

        if (!this.elements.productsWrap || !this.elements.products) return;

        const mql = window.matchMedia(FoxTheme.config?.mediaQueryMobile ?? 'screen and (max-width: 767px)');
        mql.onchange = this.init.bind(this);

        const mqlTablet = window.matchMedia(FoxTheme.config?.mediaQueryTablet ?? 'screen and (max-width: 1023px)');
        mqlTablet.onchange = this.init.bind(this);

        // Defer first init until the slider scrolls within ~200px of the viewport. Related /
        // recently-viewed sliders are below the fold on PDP, so deferring moves the Swiper-init
        // reflow off the initial-render critical path entirely. Motion.inView fires immediately
        // when the element is already in view, so this is safe for above-the-fold placements.
        // The no-op stub at theme.js:11 fires the callback right away when vendor.js failed to
        // load Motion, preserving non-deferred behavior as fallback. mql change handlers above
        // invoke init() directly (not via inView) — resize-time reflow is tolerable and we
        // can't tell from Liquid whether the slider is currently in viewport, so we skip the
        // observer round-trip on resize.
        //
        // Note: Motion.inView fires on EVERY viewport entry (and fires the cleanup function
        // returned from the callback on every exit; we don't return one). init() is idempotent
        // — initSlider's `typeof this.sliderInstance === 'object'` early-return makes repeated
        // entries no-ops. Idempotency is load-bearing here; do not remove it.
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          const currentItems = FoxTheme.config.mqlTablet ? this.tabletItems : this.items;

          if (this.totalItems > currentItems) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;
        if (!window.FoxTheme?.Swiper || !window.FoxTheme?.Carousel) return;

        // rem → px against shop-theme's 16px root (not 10px — the prior `* 10` here rendered
        // spaceBetween ~37% smaller than the CSS gap value, so Swiper slides sat closer than
        // the equivalent .f-grid would have rendered them).
        const columnGap = window.getComputedStyle(this.elements.products).getPropertyValue('--f-column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 16;

        this.sliderOptions = {
          slidesPerView: 2,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.elements.nextEl,
            prevEl: this.elements.prevEl,
          },
          pagination: {
            el: this.elements.pagination,
            type: this.paginationType,
          },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
            },
            1024: {
              slidesPerView: this.items,
            },
          },
          loop: false,
          threshold: 2,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        // Liquid renders these classes when products_to_display > columns_tablet, so on the
        // initial-load path these adds are no-ops and Swiper measures stable layout. The dynamic
        // path (mobile→desktop resize after destroySlider stripped them) needs them re-added.
        // Critically, we DO NOT remove .f-grid: the .f-grid.swiper-wrapper combo CSS rule in
        // theme.css forces display:flex (with gap:0 to avoid doubling Swiper's spaceBetween)
        // on tablet+, so Swiper's measurement matches what Liquid painted. Mobile keeps display:
        // grid via the cascade because the combo rule is media-gated, and destroySlider() strips
        // .swiper-wrapper there anyway.
        this.elements.productsWrap.classList.add(this.classes.swiper);
        this.elements.products.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this.elements.productsWrap, this.sliderOptions, [
          FoxTheme.Swiper.Mousewheel,
        ]);
        this.sliderInstance.init();
        this.handleAccessibility();
        this.fixQuickviewDuplicate();
      }

      handleAccessibility() {
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', (event) => {
            if (event.relatedTarget !== null) {
              if (element.closest('.swiper-slide')) {
                const slide = element.closest('.swiper-slide');
                // Sentry SHOPIFY-1TG: these listeners outlive destroySlider(),
                // so the instance can be gone by the time focus lands here.
                const slider = this.sliderInstance?.slider;
                if (slider) slider.slideTo(slider.slides.indexOf(slide));
              }
            } else {
              element.blur();
            }
          });
        });
      }

      destroySlider() {
        // .f-grid is permanent in markup; we only strip Swiper's classes here. On mobile that
        // returns the element to the .f-grid display:grid cascade; on desktop few-products it
        // does the same (the .f-grid.swiper-wrapper combo rule no longer applies once
        // .swiper-wrapper is gone).
        this.elements.productsWrap.classList.remove(this.classes.swiper);
        this.elements.products.classList.remove(this.classes.swiperWrapper);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      fixQuickviewDuplicate() {
        const modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
